import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import VideocamIcon from '@mui/icons-material/Videocam';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';

import {
  getCampaignElementById,
  updateCampaignElementValue,
  clearUploadedVideo,
  getCampaignElements,
  getCampaignLandingOrganizationId,
  getCampaignLanguage
} from '@src/redux/modules/campaignLandingPage';
import { SUBMISSION_TYPES, SUBMISSION_TYPE_LABELS } from '@src/constants/element-types';
import DynamicCollectors from '@src/components/CampaignLanding/DynamicCollectors';
import styles from '@src/components/CampaignLanding/SubmissionTypeSelectorField.module.less';

const ORGANIZATION_CATEGORIES_MODE = 'organization_categories';

const SUBMISSION_TYPE_ICONS = {
  [SUBMISSION_TYPES.VIDEO]: VideocamIcon,
  [SUBMISSION_TYPES.IMAGE]: ImageIcon,
  [SUBMISSION_TYPES.TEXT]: TextFieldsIcon
};

// Rebuilds the collector data object keeping only the content for the types that
// remain selected, so removed types don't leak stale image/text content into the submission.
const pruneCollectorData = (collectorData = {}, keptTypes) => {
  const next = {};
  if (keptTypes.includes(SUBMISSION_TYPES.IMAGE) && collectorData.image) {
    next.image = collectorData.image;
  }
  if (keptTypes.includes(SUBMISSION_TYPES.TEXT) && collectorData.text) {
    next.text = collectorData.text;
  }
  return next;
};

const SubmissionTypeSelectorField = ({
  _updateCampaignElementValue,
  _clearUploadedVideo,
  element,
  qid,
  campaignElements,
  organizationId,
  language
}) => {
  const isCategoryMode = element?.submissionTypeCategoryMode === ORGANIZATION_CATEGORIES_MODE;
  const categorySnapshots = element?.submissionTypeCategorySnapshots || [];
  const categoryPlaceholder = element?.submissionTypeCategoryPlaceholder || 'Select a category';
  const allowedValues = element?.allowedValues || [SUBMISSION_TYPES.VIDEO];
  const selectedTypes = campaignElements[qid]?.selectedSubmissionTypes || [];
  const selectedCategoryId = campaignElements[qid]?.categoryId || '';
  const [isTouched, setIsTouched] = useState(false);

  // Auto-select if only one option is available (manual mode only)
  useEffect(() => {
    if (!isCategoryMode && allowedValues.length === 1 && selectedTypes.length === 0) {
      _updateCampaignElementValue({
        qid,
        selectedSubmissionTypes: allowedValues,
        selected: true
      });
    }
  }, [isCategoryMode, allowedValues, selectedTypes.length, qid, _updateCampaignElementValue]);

  const handleTypeToggle = (typeId, checked) => {
    setIsTouched(true);
    const newSelectedTypes = checked
      ? [...selectedTypes, typeId]
      : selectedTypes.filter((t) => t !== typeId);

    _updateCampaignElementValue({
      qid,
      selectedSubmissionTypes: newSelectedTypes,
      selected: newSelectedTypes.length > 0
    });
  };

  // Picking a category resets any previously chosen types: the user then selects one or
  // more of the category's allowed types below (a category is a pool of options, not a
  // fixed set that's all required).
  const handleCategorySelect = (categoryId) => {
    setIsTouched(true);
    const snapshot = categorySnapshots.find((c) => c.id === categoryId);
    if (!snapshot) {
      return;
    }

    // Clear any content collected for the previously selected category's types
    const collectorData = campaignElements[qid]?.value || {};
    const prunedValue = pruneCollectorData(collectorData, []);

    if (selectedTypes.includes(SUBMISSION_TYPES.VIDEO)) {
      _clearUploadedVideo(qid);
    }

    _updateCampaignElementValue({
      qid,
      value: prunedValue,
      selectedSubmissionTypes: [],
      categoryId: snapshot.id,
      categoryName: snapshot.name,
      selected: false
    });
  };

  const selectedCategory = categorySnapshots.find((c) => c.id === selectedCategoryId);
  // In category mode the selectable types are limited to the chosen category's pool.
  const typePool = isCategoryMode ? selectedCategory?.allowedTypes || [] : allowedValues;

  // In category mode the per-type label/description come from the chosen category snapshot,
  // overriding the element defaults so the collectors show the category's custom copy.
  const effectiveElement =
    isCategoryMode && selectedCategory
      ? {
          ...element,
          typeLabels: selectedCategory.typeLabels || {},
          typeDescriptions: selectedCategory.typeDescriptions || {}
        }
      : element;

  const isValid =
    !element?.required ||
    (isCategoryMode ? !!selectedCategoryId && selectedTypes.length > 0 : selectedTypes.length > 0);
  const showError = element?.required && isTouched && !isValid;

  const renderTypeCards = (types) => (
    <Stack direction="row" flexWrap="wrap" gap={1.5} className={styles.vpOptionsContainer}>
      {types.map((typeId) => {
        const IconComponent = SUBMISSION_TYPE_ICONS[typeId];
        const isChecked = selectedTypes.includes(typeId);
        const label = SUBMISSION_TYPE_LABELS[typeId] || typeId;

        return (
          <Box
            key={typeId}
            className={`${styles.vpOptionCard} ${isChecked ? styles.vpOptionCardSelected : ''}`}
            onClick={() => handleTypeToggle(typeId, !isChecked)}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleTypeToggle(typeId, e.target.checked);
                  }}
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  {IconComponent && (
                    <IconComponent fontSize="small" color={isChecked ? 'primary' : 'action'} />
                  )}
                  <Typography variant="body2" fontWeight={isChecked ? 600 : 400}>
                    {label}
                  </Typography>
                </Stack>
              }
              sx={{ margin: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        );
      })}
    </Stack>
  );

  return (
    <section className={styles.vpSubmissionTypeSelectorContainer}>
      <Box className={styles.vpSelectorSection}>
        <label className={styles.vpLabel}>
          {element?.label || 'How would you like to submit?'}
          {element?.required && <span className={styles.vpRequiredLabel}>*</span>}
        </label>

        {isCategoryMode ? (
          <Stack gap={1.5} className={styles.vpOptionsContainer}>
            <FormControl fullWidth size="small">
              <InputLabel id={`submission-category-${qid}`}>{categoryPlaceholder}</InputLabel>
              <Select
                labelId={`submission-category-${qid}`}
                label={categoryPlaceholder}
                value={selectedCategoryId}
                onChange={(e) => handleCategorySelect(e.target.value)}
              >
                {categorySnapshots.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCategoryId && renderTypeCards(typePool)}
          </Stack>
        ) : (
          renderTypeCards(typePool)
        )}

        {showError && (
          <Typography variant="caption" color="error" className={styles.vpErrorMessage}>
            {isCategoryMode && !selectedCategoryId
              ? 'Please select a category'
              : 'Please select at least one submission type'}
          </Typography>
        )}

        {selectedTypes.length > 0 && (
          <Typography variant="caption" color="text.secondary" className={styles.vpHelpText}>
            All selected options are required to complete your submission
          </Typography>
        )}
      </Box>

      {selectedTypes.length > 0 && (
        <DynamicCollectors
          qid={qid}
          selectedTypes={selectedTypes}
          element={effectiveElement}
          campaignElements={campaignElements}
          organizationId={organizationId}
          language={language}
        />
      )}
    </section>
  );
};

SubmissionTypeSelectorField.propTypes = {
  _updateCampaignElementValue: PropTypes.func,
  _clearUploadedVideo: PropTypes.func,
  element: PropTypes.object,
  qid: PropTypes.string,
  campaignElements: PropTypes.object,
  organizationId: PropTypes.string,
  language: PropTypes.string
};

function mapStateToProps(state, ownProps) {
  return {
    element: getCampaignElementById(state, ownProps.qid),
    campaignElements: getCampaignElements(state),
    organizationId: getCampaignLandingOrganizationId(state),
    language: getCampaignLanguage(state)
  };
}

const mapDispatchToProps = {
  _updateCampaignElementValue: updateCampaignElementValue,
  _clearUploadedVideo: clearUploadedVideo
};

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionTypeSelectorField);



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/SubmissionTypeSelectorField.jsx